/* Swiper navigation, native video playback and the shared Shopify cart pipeline. */
(function () {
  'use strict';
  let swiperLoad, activeViewer, prepared;
  function releasePrepared() {
    if (!prepared) return;
    clearTimeout(prepared.expiry);
    const video = prepared.video; prepared = null;
    video.pause(); video.removeAttribute('src'); video.load(); video.remove();
  }
  function loadSwiper(url) {
    if (window.CuSwiper) return Promise.resolve(window.CuSwiper);
    if (!swiperLoad) swiperLoad = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const fail = () => { clearTimeout(timer); script.remove(); reject(new Error('The video viewer could not load. Please try again.')); };
      const timer = setTimeout(fail, 45000);
      script.src = url;
      script.onload = () => { clearTimeout(timer); window.CuSwiper ? resolve(window.CuSwiper) : fail(); };
      script.onerror = fail; document.head.append(script);
    }).catch(error => { swiperLoad = null; throw error; });
    return swiperLoad;
  }
  function button(label, text, className) {
    const el = document.createElement('button');
    el.type = 'button'; el.className = className; el.setAttribute('aria-label', label); el.textContent = text;
    return el;
  }
  function arrowButton(label, direction) {
    const el = button(label, '', 'cu-reel-player__nav');
    const path = direction === 'up' ? 'M8 13.5v-11M3.5 7 8 2.5 12.5 7' : 'M8 2.5v11M3.5 9 8 13.5 12.5 9';
    el.innerHTML = '<svg class="cu-icon cu-icon--arrow" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="' + path + '" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return el;
  }
  function time(seconds) {
    const value = Math.max(0, Math.floor(seconds || 0));
    return Math.floor(value / 60) + ':' + String(value % 60).padStart(2, '0');
  }
  class CuReels extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); this.muted = true;
      const warm = () => {
        if (!this.conserveData()) loadSwiper(this.dataset.swiperSrc).catch(() => {});
      };
      for (const event of ['pointerover', 'pointerdown', 'focusin']) this.addEventListener(event, event => {
        warm();
        const link = event.target.closest('[data-cu-reel-video]');
        if (link) this.prepareClip(link, true);
      }, { signal: this.abort.signal, passive: true });
      if ('IntersectionObserver' in window) {
        this.nearby = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { warm(); this.nearby.disconnect(); }
        }, { rootMargin: '800px' });
        this.nearby.observe(this);
        this.visibleClips = new Set();
        this.clipObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) this.visibleClips.add(entry.target); else this.visibleClips.delete(entry.target); });
          const first = this.links().find(link => this.visibleClips.has(link));
          if (first) this.prepareClip(first);
          else if (prepared?.owner === this) releasePrepared();
        }, { rootMargin: '200px 0px', threshold: 0.15 });
        this.links().forEach(link => this.clipObserver.observe(link));
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden && prepared?.owner === this) releasePrepared(); }, { signal: this.abort.signal });
      // One likely opening clip per page, after the main page has loaded.
      const idleWarm = () => {
        this.idleWarm = setTimeout(() => {
          if (!prepared && this.isConnected && document.querySelector('cu-reels') === this) this.prepareClip(this.links()[0]);
        }, 1500);
      };
      if (document.readyState === 'complete') idleWarm();
      else window.addEventListener('load', idleWarm, { once: true, signal: this.abort.signal });
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-cu-reel-video]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); this.open(link);
      }, { signal: this.abort.signal });
      const shared = this.links().find(link => location.hash === '#cu-reel-' + link.dataset.cuReelId);
      if (shared) this.open(shared);
    }
    links() { return Array.from(this.querySelectorAll('[data-cu-reel-video]')); }
    conserveData() { return navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || ''); }
    lightSource() {
      const connection = navigator.connection;
      return this.conserveData() || connection?.effectiveType === '3g' || (connection?.downlink > 0 && connection.downlink < 2);
    }
    source(video) { return this.lightSource() && video.dataset.lightSrc ? video.dataset.lightSrc : video.dataset.src; }
    prepareClip(link, intent = false) {
      if (!link || this.conserveData() || document.hidden || activeViewer?.dialog || Date.now() < (this.suppressWarmUntil || 0)) return;
      const template = link.querySelector('template')?.content.querySelector('video');
      if (!template) return;
      const src = this.source(template);
      if (prepared?.video.getAttribute('src') === src) return;
      if (!intent && prepared?.intent && Date.now() - prepared.at < 2000) return;
      releasePrepared();
      const video = template.cloneNode(true); video.muted = true; video.preload = 'auto';
      video.dataset.cuReelPreload = '';
      video.setAttribute('aria-hidden', 'true'); video.tabIndex = -1;
      video.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.append(video);
      prepared = { video, owner: this, src, intent, at: Date.now(), expiry: setTimeout(releasePrepared, 120000) };
      video.addEventListener('error', () => { if (prepared?.video === video) releasePrepared(); });
      video.src = src; video.load();
    }
    notice(message) {
      let status = this.querySelector('[data-reels-notice]');
      if (!status) { status = document.createElement('p'); status.dataset.reelsNotice = ''; status.className = 'cu-reels-notice'; status.setAttribute('role', 'status'); this.append(status); }
      status.textContent = message; status.hidden = !message;
    }
    async open(link) {
      if (this.dialog) return;
      this.notice(''); link.setAttribute('aria-busy', 'true');
      let openingDialog;
      try {
        if (!this.isConnected) return;
        activeViewer?.dismiss(); activeViewer = this; this.opener = link; this.active = -1; this.navigationUnavailable = false;
        const links = this.links(), dialog = document.createElement('dialog');
        openingDialog = dialog;
        dialog.className = 'cu-reel-player'; dialog.setAttribute('aria-label', 'ClearUp video stories'); this.dialog = dialog;
        const stage = document.createElement('div'); stage.className = 'cu-reel-player__stage';
        const feed = document.createElement('div'); feed.className = 'swiper cu-reel-player__feed';
        const wrapper = document.createElement('div'); wrapper.className = 'swiper-wrapper';
        this.stories = links.map(item => {
          const story = item.querySelector('template').content.firstElementChild.cloneNode(true);
          const placeholder = story.querySelector('video');
          if (item === link && prepared?.src === this.source(placeholder)) {
            clearTimeout(prepared.expiry);
            // Move the already-buffered element; cloning it would discard its media buffer.
            prepared.video.removeAttribute('data-cu-reel-preload'); prepared.video.removeAttribute('style');
            prepared.video.removeAttribute('aria-hidden'); prepared.video.removeAttribute('tabindex');
            placeholder.replaceWith(prepared.video); prepared = null;
          }
          story.dataset.reelId = item.dataset.cuReelId; wrapper.append(story); return story;
        });
        releasePrepared();
        this.videos = this.stories.map(story => story.querySelector('video'));
        feed.append(wrapper); stage.append(feed);
        const close = button('Close videos', '', 'cu-reel-player__close swiper-no-swiping');
        close.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="m5 5 14 14m0-14L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        close.addEventListener('click', () => this.dismiss());
        this.counter = document.createElement('span'); this.counter.className = 'cu-reel-player__counter'; this.counter.setAttribute('aria-live', 'polite');
        this.prev = arrowButton('Previous video', 'up'); this.next = arrowButton('Next video', 'down');
        this.prev.addEventListener('click', () => this.goTo(this.active - 1)); this.next.addEventListener('click', () => this.goTo(this.active + 1));
        const navigation = document.createElement('div'); navigation.className = 'cu-reel-player__navigation'; navigation.append(this.prev, this.next);
        stage.append(close, this.counter); dialog.append(stage, navigation);
        this.pageVideos = Array.from(document.querySelectorAll('video')).filter(video => !video.paused);
        this.pageVideos.forEach(video => video.pause());
        document.body.append(dialog); dialog.showModal(); window.CU.lockScroll(); this.locked = true;
        dialog.addEventListener('close', () => { if (this.dialog === dialog) this.cleanup(); });
        dialog.addEventListener('click', event => { if (event.target === dialog) this.dismiss(); });
        this.session = new AbortController();
        this.stories.forEach((story, index) => this.bindStory(story, index));
        dialog.addEventListener('keydown', event => {
          if (event.target.matches('input, select, textarea')) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); event.stopPropagation();
            this.goTo(this.active + (event.key === 'ArrowDown' ? 1 : -1));
          }
        });
        document.addEventListener('visibilitychange', () => {
          const video = this.videos[this.active];
          if (document.hidden) { this.resumeOnVisible = !video.paused; video.pause(); }
          else if (this.resumeOnVisible) { this.resumeOnVisible = false; this.play(this.active); }
        }, { signal: this.session.signal });
        const owner = this;
        window.addEventListener('resize', () => { if (this.active >= 0) this.updateCaption(this.stories[this.active]); }, { signal: this.session.signal });
        // Open and start playback immediately; navigation code loads independently.
        this.goTo(links.indexOf(link)); close.focus({ preventScroll: true });
        let Swiper;
        try { Swiper = await loadSwiper(this.dataset.swiperSrc); }
        catch (error) {
          if (this.dialog === dialog) { this.navigationUnavailable = true; this.status(this.stories[this.active], ''); }
          return;
        }
        if (this.dialog !== dialog) return;
        this.swiper = new Swiper(feed, {
          init: false, direction: 'vertical', slidesPerView: 1, initialSlide: this.active,
          speed: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280,
          threshold: 10, touchStartPreventDefault: false, noSwiping: true,
          mousewheel: { forceToAxis: true, thresholdDelta: 15, thresholdTime: 350 },
          a11y: { enabled: true, containerMessage: 'Video stories. Swipe or use up and down arrows to change video.', slideLabelMessage: 'Video {{index}} of {{slidesLength}}' },
          on: { slideChange() { owner.activate(this.activeIndex); } },
        });
        this.swiper.init(); this.activate(this.swiper.activeIndex);
      } catch (error) {
        if (this.dialog !== openingDialog) return;
        if (this.dialog) this.dismiss();
        if (this.isConnected) this.notice(error.message || 'The video could not open. Please try again.');
      } finally { link.removeAttribute('aria-busy'); }
    }
    status(story, message) {
      const el = story.querySelector('[data-reel-status]');
      message = message || (this.navigationUnavailable ? 'Swipe navigation could not load. Use the arrow buttons.' : '');
      el.textContent = message; el.hidden = !message;
    }
    bindStory(story, index) {
      const video = this.videos[index], play = story.querySelector('[data-reel-play]'), seek = story.querySelector('[data-reel-seek]');
      const state = () => { story.classList.toggle('is-paused', video.paused); play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video'); if (video.paused && this.active === index) clearTimeout(this.nextLoad); };
      video.addEventListener('play', state); video.addEventListener('pause', state);
      video.addEventListener('waiting', () => story.classList.add('is-buffering'));
      video.addEventListener('playing', () => {
        story.classList.remove('is-buffering'); this.status(story, '');
        // Give the current video priority before downloading the next one.
        if (index === this.active) this.queueNext(index);
      });
      video.addEventListener('error', () => {
        if (video.dataset.fallbackSrc && !video.dataset.fallbackUsed && video.getAttribute('src') !== video.dataset.fallbackSrc) {
          video.dataset.fallbackUsed = 'true'; video.src = video.dataset.fallbackSrc; video.load();
          if (index === this.active && this.dialog) this.play(index);
          return;
        }
        story.classList.remove('is-buffering'); this.status(story, 'This video could not load. Try the next video.');
      });
      const progress = () => {
        if (!Number.isFinite(video.duration) || !video.duration) return;
        seek.disabled = false; seek.value = video.currentTime / video.duration * 100;
        seek.style.setProperty('--progress', seek.value + '%');
        seek.setAttribute('aria-valuetext', time(video.currentTime) + ' of ' + time(video.duration));
      };
      video.addEventListener('timeupdate', progress); video.addEventListener('loadedmetadata', progress);
      seek.addEventListener('input', () => { if (Number.isFinite(video.duration)) video.currentTime = Number(seek.value) / 100 * video.duration; });
      play.addEventListener('click', () => { if (this.active === index) { if (video.paused) this.play(index); else video.pause(); } });
      story.querySelector('[data-reel-sound]').addEventListener('click', () => { this.muted = !this.muted; this.updateSound(); });
      story.querySelector('[data-reel-share]').addEventListener('click', () => this.share(story));
      const captionToggle = story.querySelector('[data-reel-caption-toggle]');
      if (captionToggle) {
        const caption = story.querySelector('.cu-reel-player__caption');
        caption.id = 'cu-caption-' + story.dataset.reelId;
        captionToggle.setAttribute('aria-controls', caption.id);
        captionToggle.addEventListener('click', () => {
          const expanded = captionToggle.getAttribute('aria-expanded') !== 'true';
          caption.parentElement.classList.toggle('is-expanded', expanded);
          captionToggle.setAttribute('aria-expanded', String(expanded));
          captionToggle.setAttribute('aria-label', expanded ? 'Collapse caption' : 'Show full caption');
          captionToggle.textContent = expanded ? 'less' : 'more';
          if (!expanded) { caption.scrollTop = 0; this.updateCaption(story); }
        });
      }
      video.querySelector('track')?.addEventListener('load', event => {
        // Keep automatically positioned captions above the product overlay.
        Array.from(event.target.track.cues || []).forEach(cue => {
          if (cue.line === 'auto') { cue.snapToLines = false; cue.line = 55; cue.size = 75; }
        });
      });
      story.querySelector('[data-reel-captions]')?.addEventListener('click', event => {
        const control = event.currentTarget, show = control.getAttribute('aria-pressed') !== 'true';
        Array.from(video.textTracks).forEach(track => { track.mode = show ? 'showing' : 'hidden'; });
        control.setAttribute('aria-pressed', String(show)); control.setAttribute('aria-label', show ? 'Hide captions' : 'Show captions');
      });
      const form = story.querySelector('[data-reel-product-form]');
      if (form) {
        form.querySelector('[data-reel-variant]')?.addEventListener('change', event => {
          const option = event.target.selectedOptions[0], add = form.querySelector('[type="submit"]');
          form.querySelector('[data-reel-price]').textContent = option.dataset.price;
          add.disabled = option.dataset.available !== 'true'; add.textContent = add.disabled ? 'Sold out' : 'Add to bag';
        });
        form.addEventListener('submit', event => {
          if (!window.CU?.addItems) return;
          event.preventDefault(); event.stopPropagation();
          if (form.querySelector('[type="submit"]').disabled) return;
          const variant = new FormData(form).get('id'); this.dismiss(false);
          // Same bundled add, gift and discount path as every product form.
          // The detached form retains its error slot for relaying a failure.
          window.CU.addItems(form, [{ id: variant, quantity: 1 }])?.then(() => {
            const error = form.querySelector('[data-cu-add-error]');
            if (!error.hidden) { this.notice(error.textContent); this.opener?.focus({ preventScroll: true }); }
          });
        });
      }
      state();
    }
    updateCaption(story) {
      const caption = story.querySelector('.cu-reel-player__caption'), toggle = story.querySelector('[data-reel-caption-toggle]');
      if (!caption || !toggle) return;
      if (toggle.getAttribute('aria-expanded') === 'true') return;
      // Measure at full width first so hiding the button does not leave a stale gap.
      toggle.hidden = true;
      toggle.hidden = caption.scrollHeight <= caption.clientHeight + 1;
    }
    updateSound() {
      this.stories.forEach((story, index) => {
        this.videos[index].muted = this.muted;
        const control = story.querySelector('[data-reel-sound]');
        control.setAttribute('aria-label', this.muted ? 'Turn sound on' : 'Mute video'); control.setAttribute('aria-pressed', String(!this.muted));
        story.querySelector('[data-sound-off]').hidden = !this.muted; story.querySelector('[data-sound-on]').hidden = this.muted;
        story.querySelector('[data-sound-label]').textContent = this.muted ? 'Sound' : 'Mute';
      });
    }
    goTo(index) {
      index = Math.max(0, Math.min(this.stories.length - 1, index));
      if (this.swiper) { this.swiper.slideTo(index); return; }
      this.stories[index].parentElement.style.transform = 'translate3d(0, ' + (-index * 100) + '%, 0)';
      this.stories.forEach((story, i) => story.classList.toggle('swiper-slide-active', i === index));
      this.activate(index);
    }
    loadVideo(index, preload) {
      const video = this.videos[index];
      if (!video) return;
      if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
      video.preload = preload;
      if (!video.getAttribute('src')) { video.src = this.source(video); video.load(); }
    }
    bufferedAhead(video) {
      if (!video) return 0;
      for (let i = 0; i < video.buffered.length; i++) {
        if (video.buffered.start(i) <= video.currentTime && video.buffered.end(i) >= video.currentTime) return video.buffered.end(i) - video.currentTime;
      }
      return 0;
    }
    lookAhead() {
      const connection = navigator.connection;
      return !this.lightSource() && connection?.downlink >= 5 ? 2 : 1;
    }
    queueNext(index) {
      clearTimeout(this.nextLoad);
      if (this.conserveData()) return;
      const prepare = () => {
        if (!this.dialog || this.active !== index || document.hidden || this.conserveData()) return;
        const video = this.videos[index], remaining = video.duration - video.currentTime;
        if (!video.paused && this.bufferedAhead(video) >= Math.min(3, remaining || 3)) {
          this.loadVideo(index + 1, 'auto');
          const next = this.videos[index + 1];
          if (this.lookAhead() === 2 && this.bufferedAhead(video) >= Math.min(6, remaining || 6) && this.bufferedAhead(next) >= 3) {
            this.loadVideo(index + 2, 'auto'); return;
          }
          if (this.lookAhead() === 1 || !this.videos[index + 2]) return;
        }
        this.nextLoad = setTimeout(prepare, 250);
      };
      this.nextLoad = setTimeout(prepare, 0);
    }
    activate(index) {
      if (index === this.active) return;
      clearTimeout(this.nextLoad);
      this.active = index;
      this.stories.forEach((story, i) => {
        const video = this.videos[i]; story.inert = i !== index;
        const captionToggle = story.querySelector('[data-reel-caption-toggle]');
        if (captionToggle) {
          const caption = story.querySelector('.cu-reel-player__caption');
          caption.parentElement.classList.remove('is-expanded'); caption.scrollTop = 0;
          captionToggle.setAttribute('aria-expanded', 'false'); captionToggle.setAttribute('aria-label', 'Show full caption'); captionToggle.textContent = 'more';
        }
        if (i !== index) { video.pause(); video.preload = 'none'; }
        if ((i < index - 1 || i > index + this.lookAhead()) && video.getAttribute('src')) { video.removeAttribute('src'); video.removeAttribute('poster'); video.load(); }
      });
      this.loadVideo(index, 'auto');
      this.counter.textContent = (index + 1) + ' / ' + this.stories.length;
      this.prev.disabled = index === 0; this.next.disabled = index === this.stories.length - 1;
      this.updateCaption(this.stories[index]);
      this.updateSound(); this.play(index);
    }
    play(index) {
      const video = this.videos[index];
      if (video.error) video.load();
      this.status(this.stories[index], '');
      video.play().catch(error => {
        if (!this.dialog || index !== this.active || error.name === 'AbortError') return;
        this.stories[index].classList.remove('is-buffering');
        this.status(this.stories[index], video.error ? 'This video could not load. Try the next video.' : 'Tap the video to play.');
      });
    }
    async share(story) {
      const url = new URL(location.href); url.hash = 'cu-reel-' + story.dataset.reelId;
      try {
        if (navigator.share) await navigator.share({ title: 'ClearUp video', url: url.href });
        else if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(url.href); this.status(story, 'Link copied.'); }
        else throw new Error('Copy manually');
      } catch (error) {
        if (error.name === 'AbortError') return;
        const field = story.querySelector('[data-reel-share-url]'); field.value = url.href; field.hidden = false; field.focus(); field.select();
        this.status(story, 'Copy this link to share.');
      }
    }
    dismiss(restoreFocus = true) { if (this.dialog) { this.restoreFocus = restoreFocus; this.dialog.close(); this.cleanup(); } }
    cleanup() {
      if (!this.dialog) return;
      this.suppressWarmUntil = Date.now() + 1000;
      clearTimeout(this.nextLoad);
      this.session?.abort(); this.swiper?.destroy(true, true); this.swiper = null;
      this.videos?.forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); });
      this.dialog.remove(); this.dialog = null;
      if (this.locked) { window.CU.unlockScroll(); this.locked = false; }
      this.pageVideos?.forEach(video => { if (video.isConnected && !document.hidden) video.play().catch(() => {}); });
      if (this.restoreFocus !== false && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.restoreFocus = true; if (activeViewer === this) activeViewer = null;
    }
    disconnectedCallback() { clearTimeout(this.idleWarm); this.abort?.abort(); this.nearby?.disconnect(); this.clipObserver?.disconnect(); if (prepared?.owner === this) releasePrepared(); this.abort = null; this.dismiss(false); }
  }
  if (!customElements.get('cu-reels')) customElements.define('cu-reels', CuReels);
})();
